/* eslint-disable max-lines */
import React, { FC, ReactElement, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useSelector } from 'react-redux';
import { LocationDescriptorObject } from 'history';
import { header1 } from '@westwing/ui-kit/typography';
import { forDesktop, forIE, forWideScreens, forMidDesktop, forMidWideScreens } from '@westwing/ui-kit/breakPoints';
import { AspectRatio } from '@westwing/ui-kit/AspectRatio';
import ErrorBoundary from '../Generic/ErrorBoundaries';
import GenericErrorRetry from '../Generic/ErrorPages/GenericErrorRetry';
import useFetchingAction from '../../hooks/useFetchingAction/useFetchingAction';
import { fetchHomePageCmsDataIfNeeded } from '../../redux/homepage/action';
import StateInterface from '../../redux/types';
import { CrossCategoryNavigation as CrossCatNavInterface } from '../../redux/products/types';
import { fetchBestSellerProducts, fetchNewRandomizedProducts } from '../../redux/products/actions';
import { combineThunks } from '../../redux/utils';
import useConfig from '../../hooks/useConfig';
import { getCategoriesUrl } from '../../urlService';
import { HomepageItems } from '../../redux/homepage/types';
import { SEO_CONSTANTS } from '../ListingPage/services/seo/constant';
import GenericSeoHead from '../Generic/SeoHead';
import { homePageheroBannerRatios, homePageSecondaryBannerRatios } from '../../constants/aspectRatios';
import LineSeparator from '../Generic/LineSeparator';
import { homePageHrefLang } from '../ListingPage/services/seo';
import { fetchHomePageSeoDataIfNeeded } from '../../redux/seo/actions';
import { SeoData } from '../../redux/seo/types';
import useRouter from '../../hooks/useRouter';
import useGa from '../../hooks/useGa';
import { TRACKING_PAGE_LABELS } from '../ListingPage/hooks/useTrackingPageLabel';
import { CategoryData } from '../../redux/category/types';
import PlacingBanners from './PlacingBanner';
import PermanentBanners from './PermanentBanner';
import CategorySliders from './CategorySlider';
import PortraitBanners from './PortraitBanners';
import { SecondaryBannerImg, BannerText } from './SecondaryBanner';
import HeroBanner from './HeroBanner';
import CategoriesBubbleSlider from './CategoriesBubbleSlider';
import LooksSlider from './LooksSlider';
import LooksSliderPromotion from './LooksSliderPromotion';
import NewProductsSlider from './NewProductsSlider';
import UGGalleryBanner from './UGGalleryBanner';
import ReferAFriendBanner from './ReferAFriendBanner';
import BestSellerProductsSlider from './BestSellerProductsSlider';
import SingleLookPromotion from './SingleLookPromotion';
import { BellaTestIds } from 'Common/qaIds';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import useLoginAndRegisterOverlayWithTracking from 'Client/hooks/useLoginAndRegisterOverlayWithTracking';
import { fetchLooksSliderIfNeeded } from 'Client/redux/looksSlider/actions';

export const LOGIN_ON = '?login=on';

const PlacingAndSecondaryBannerWrapper = styled.div`
    display: grid;
    margin: 0 20px;
    grid-template:
        'placing    ' minmax(247px, max-content)
        'refer-friend ' auto
        '.            ' 20px
        'secondary    ' auto
        'text         ' auto
        / 100%;
    ${forDesktop`
        margin: 0 40px;
        grid-template:
            'secondary     .             placing    ' auto
            'text          text          text         ' auto
            'refer-friend  refer-friend  refer-friend ' auto
            / 1fr 10px 1fr;
    `}

    ${forIE`
        display: -ms-grid;
        -ms-grid-columns:  1fr 10px 1fr;
        -ms-grid-rows:  auto auto auto;
    `}
`;

const PBannersWrapper = styled.div`
    ${forDesktop`
        margin: 0 20px;
    `}
`;

const StyledSecondaryBannerImg = styled(SecondaryBannerImg)`
    ${AspectRatio}
    grid-area: secondary;
    ${forIE`
        -ms-grid-row: 1;
        -ms-grid-column: 1;
    `}
`;

const StyledBannerText = styled(BannerText)`
    grid-area: text;

    ${forDesktop`
        max-width: 45%;
    `}
    ${forIE`
    -ms-grid-row: 2;
    -ms-grid-column: 1;
    -ms-grid-column-span: 3;
`}
`;

const PlacingBannersWrapper = styled.div`
    display: grid;
    grid-template-rows: auto;
    grid-row-gap: 5px;
    ${forDesktop`
        grid-area: placing;
        display: flex;
        flex-basis: auto;
        flex-flow: column;
        justify-content: space-between;
    `}
    ${forIE`
        -ms-grid-row: 1;
        -ms-grid-column: 3;
    `}
`;

export const ProductsScrollWrapper = styled.div<{ p100IncreasedDesktopPageWidth?: boolean }>`
    height: 410px;
    margin: 0 0 40px 0;
    ${forDesktop`
        margin: 0 20px 40px 20px;
    `}
    ${forMidDesktop`
        height: 459px;
    `}
    ${forWideScreens`
        height: 609px;
    `}

    ${({ p100IncreasedDesktopPageWidth }) =>
        p100IncreasedDesktopPageWidth &&
        css`
            ${forMidWideScreens`
                 height: auto;
                 margin-bottom: 60px;
            `}
        `}
`;

const ContentWrapper = styled.div`
    padding: 20px 0 0 0;
    display: flex;
    flex-direction: column;

    ${forDesktop`
        padding: 40px 0 0 0;
    `}
`;

const StyledLineSeparator = styled(LineSeparator)`
    width: calc(100% - 20px);
    margin-left: 20px;
    ${forDesktop`
        width: calc(100% - 40px);
        margin: 0 20px;
    `}
    padding-bottom: 10px;
`;

const StyledScrollContainer = styled.div<{ hideOnIE?: boolean }>`
    ${forDesktop`
        margin: 0 20px;
    `}
    ${({ hideOnIE }) =>
        hideOnIE
            ? forIE`
        display: none;
    `
            : forIE`
        max-height: 585px;
    `}
`;

const UGCContainer = styled.div<{ p100IncreasedDesktopPageWidth?: boolean }>`
    ${forDesktop`
        padding: 0 20px;
    `}
    ${forIE`
        height: 312px;
    `}

    ${({ p100IncreasedDesktopPageWidth }) =>
        p100IncreasedDesktopPageWidth &&
        css`
            ${forIE`
                 height: 390px
            `}
        `}
`;

// Yes its an empty div but its IMPORTANT. It is needed to contain the PlacingBanners Component on Safari 10.1
const Wrapper = styled.div``;

const HomePageHeroBanner = styled.div`
    ${AspectRatio}
`;

const shouldOpenLoginOverlay = (
    location: LocationDescriptorObject,
    isFetching: boolean,
    didFinishOnce: boolean
): boolean => {
    const { search } = location;
    return search === LOGIN_ON && !isFetching && didFinishOnce;
};

// eslint-disable-next-line complexity
const HomePage: FC<{}> = (): ReactElement => {
    const newRandomizedProductsPageSize = 10;
    const config = useConfig();
    const gaTracking = useGa();
    const { p100SingleLookPromotionHomepage, p100IncreasedDesktopPageWidth } = useFeatureToggle();
    const { showLoginAndRegisterOverlay, trackLoginOverlayShow } = useLoginAndRegisterOverlayWithTracking();

    const visibleOriginalCategories = useSelector<StateInterface, CategoryData[]>(
        state => state.categories.visibleOriginalCategories
    );
    const didFinishOnce = useSelector<StateInterface, boolean>(state => state.appShellLogin.didFinishOnce);
    const isFetching = useSelector<StateInterface, boolean>(state => state.appShellLogin.isFetching);
    const homepageItems = useSelector<StateInterface, HomepageItems>(state => state.homepage.homepageItems);
    const homePageSeoData = useSelector<StateInterface, SeoData>(state => state.homePageSeoData.seoData);

    const firstLevelCategories: CrossCatNavInterface = {
        categories: visibleOriginalCategories.map(category => {
            return { name: category.name, link: getCategoriesUrl(category.url), isSelected: false };
        }),
        parentCategory: { link: '', isRoot: true },
    };

    const {
        heroBanner,
        categoriesSlider,
        placingBanners,
        portraitBanners,
        secondaryBanner,
        ugcGalleryBanner,
        referAFriendBanner,
        permanentBanners,
    } = homepageItems;
    const { seoTitle, seoDescription, similarPages } = homePageSeoData;

    const {
        countryCode,
        currency,
        links: { url: host },
    } = config;
    const thunks = [
        fetchHomePageCmsDataIfNeeded({ countryCode, currency }),
        fetchNewRandomizedProducts(newRandomizedProductsPageSize, { countryCode, currency }),
        fetchBestSellerProducts(newRandomizedProductsPageSize, { countryCode, currency }),
        fetchHomePageSeoDataIfNeeded(),
        fetchLooksSliderIfNeeded(),
    ];
    useFetchingAction(combineThunks(...thunks));
    const hrefLangs = homePageHrefLang(config);
    const openGraphImage = heroBanner.mobileImage || heroBanner.desktopImage;

    // New products slider

    const { location } = useRouter();
    const showShowLoginOverlay = shouldOpenLoginOverlay(location, isFetching, didFinishOnce);

    useEffect(() => {
        if (showShowLoginOverlay) {
            showLoginAndRegisterOverlay({ isLogin: true });
            trackLoginOverlayShow({ isLogin: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showShowLoginOverlay]);

    useEffect(() => {
        gaTracking.trackPageView({ pageType: TRACKING_PAGE_LABELS.HOME });
        // Only fire once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showUgc = !!(ugcGalleryBanner && ugcGalleryBanner.mobileImage && ugcGalleryBanner.targetUrl);

    const bottomCategories = similarPages.length
        ? similarPages.map(item => ({
              name: item.similar_page_text,
              link: item.similar_page_link,
              isSelected: false,
          }))
        : firstLevelCategories.categories;

    const NewProductsSliderComponent = () => (
        <ProductsScrollWrapper
            data-testid={BellaTestIds.homepageNewProducts}
            p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
        >
            <StyledLineSeparator />
            <NewProductsSlider />
        </ProductsScrollWrapper>
    );

    const BestSellerProductsSliderComponent = () => (
        <ProductsScrollWrapper
            data-testid={BellaTestIds.homepageBestSellerProducts}
            p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}
        >
            <StyledLineSeparator />
            <BestSellerProductsSlider />
        </ProductsScrollWrapper>
    );

    return (
        <ErrorBoundary boundaryName="HomePage" fallbackComponent={<GenericErrorRetry />}>
            <div>
                <GenericSeoHead
                    pageTitle={seoTitle || SEO_CONSTANTS.siteName}
                    pageDescription={seoDescription || SEO_CONSTANTS.siteName}
                    robotsIndexAbleContent={SEO_CONSTANTS.indexable}
                    links={hrefLangs}
                    pageUrl={host}
                    config={config}
                    type={SEO_CONSTANTS.ogTypeHomeContentValue}
                    openGraphImage={openGraphImage}
                />

                <ErrorBoundary boundaryName="Homepage CrossCategoryNav" fallbackComponent={null}>
                    <CategoriesBubbleSlider categories={firstLevelCategories.categories} />
                </ErrorBoundary>

                <HomePageHeroBanner ratio={homePageheroBannerRatios}>
                    <HeroBanner {...heroBanner} />
                </HomePageHeroBanner>
                <ContentWrapper>
                    <Wrapper>
                        <PlacingAndSecondaryBannerWrapper>
                            <PlacingBannersWrapper>
                                <PlacingBanners placingBanners={placingBanners} />
                            </PlacingBannersWrapper>
                            <StyledSecondaryBannerImg {...secondaryBanner} ratio={homePageSecondaryBannerRatios} />

                            <StyledBannerText {...secondaryBanner} desktopTypography={header1} />
                            <ReferAFriendBanner referAFriendData={referAFriendBanner} />
                        </PlacingAndSecondaryBannerWrapper>
                    </Wrapper>
                    {p100SingleLookPromotionHomepage && (
                        <>
                            <StyledScrollContainer hideOnIE>
                                <StyledLineSeparator />
                                <SingleLookPromotion />
                            </StyledScrollContainer>
                            <LooksSliderPromotion />
                        </>
                    )}
                    <StyledScrollContainer>
                        <StyledLineSeparator />
                        <CategorySliders categories={categoriesSlider} />
                    </StyledScrollContainer>
                    <NewProductsSliderComponent />
                    <PBannersWrapper>
                        <StyledLineSeparator />
                        <PortraitBanners portraitBanners={portraitBanners} />
                    </PBannersWrapper>
                    {!p100SingleLookPromotionHomepage && (
                        <StyledScrollContainer>
                            <StyledLineSeparator />
                            <LooksSlider />
                        </StyledScrollContainer>
                    )}
                    {showUgc && (
                        <UGCContainer p100IncreasedDesktopPageWidth={p100IncreasedDesktopPageWidth}>
                            <StyledLineSeparator />
                            <UGGalleryBanner ugc={ugcGalleryBanner} />
                        </UGCContainer>
                    )}
                    <BestSellerProductsSliderComponent />
                    {permanentBanners.length === 3 && (
                        <PBannersWrapper>
                            <StyledLineSeparator />
                            <PermanentBanners banners={permanentBanners} />
                        </PBannersWrapper>
                    )}
                </ContentWrapper>

                <ErrorBoundary boundaryName="Homepage CrossCategoryNav Bottom" fallbackComponent={null}>
                    <CategoriesBubbleSlider categories={bottomCategories} isBottom />
                </ErrorBoundary>
            </div>
        </ErrorBoundary>
    );
};

export default HomePage;
